import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Ticket, CalendarCheck, Sparkles } from "lucide-react";
import api, { fileUrl, img, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Member() {
  const { t, L } = useLang();
  const { user, checkAuth } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [activity, setActivity] = useState({ workshops: [] });
  const [redemptions, setRedemptions] = useState([]);

  const load = () => {
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/my/coupons").then((r) => setMyCoupons(r.data)).catch(() => {});
    api.get("/gifts").then((r) => setGifts(r.data)).catch(() => {});
    api.get("/my/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/my/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const claim = async (c) => {
    try { await api.post(`/coupons/${c.id}/claim`); toast.success(t("claimed")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const redeem = async (g) => {
    try { await api.post(`/gifts/${g.id}/redeem`); toast.success(L("兑换成功！", "Redeemed!")); load(); checkAuth(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const claimedIds = new Set(myCoupons.map((mc) => mc.coupon_id));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="bg-[#FFD93D] fk-border-thick rounded-2xl p-8 fk-shadow flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono-fk text-xs tracking-widest">{t("member_title").toUpperCase()}</div>
          <h1 className="font-display text-4xl mt-1">{user?.name}</h1>
          <p className="text-sm text-black/60 mt-1">{user?.email}</p>
        </div>
        <div className="text-right">
          <div className="font-mono-fk text-xs">{t("points")}</div>
          <div className="font-display text-5xl flex items-center gap-2"><Sparkles size={30} /> {user?.points ?? 0}</div>
        </div>
      </div>

      <Tabs defaultValue="coupons" className="mt-8">
        <TabsList className="fk-border rounded-full bg-white p-1">
          <TabsTrigger value="coupons" data-testid="tab-coupons" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><Ticket size={15} className="mr-1" />{t("coupons")}</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white">{t("my_coupons")}</TabsTrigger>
          <TabsTrigger value="gifts" data-testid="tab-gifts" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><Gift size={15} className="mr-1" />{t("gifts")}</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><CalendarCheck size={15} className="mr-1" />{t("my_activity")}</TabsTrigger>
          <TabsTrigger value="redeemed" data-testid="tab-redeemed" className="rounded-full data-[state=active]:bg-black data-[state=active]:text-white"><Gift size={15} className="mr-1" />{t("redeemed_history")}</TabsTrigger>
        </TabsList>

        <TabsContent value="coupons" className="mt-6 grid sm:grid-cols-2 gap-5">
          {coupons.map((c) => (
            <div key={c.id} className="bg-white fk-border-thick rounded-2xl p-6 fk-shadow flex justify-between items-center gap-4">
              <div>
                <h3 className="font-display text-xl">{L(c.title_zh, c.title_en)}</h3>
                <p className="text-sm text-black/60 mt-1">{L(c.description_zh, c.description_en)}</p>
                <span className="font-mono-fk text-xs fk-border rounded px-2 py-0.5 mt-2 inline-block bg-[#00E5FF]">{c.code}</span>
              </div>
              <button disabled={claimedIds.has(c.id)} onClick={() => claim(c)} data-testid={`claim-${c.id}`} className="fk-border rounded-full px-4 py-2 text-sm font-semibold bg-[#FFD93D] fk-lift disabled:opacity-40 whitespace-nowrap">
                {claimedIds.has(c.id) ? t("claimed") : t("claim")}
              </button>
            </div>
          ))}
          {coupons.length === 0 && <p className="text-black/50 font-mono-fk">暂无优惠券</p>}
        </TabsContent>

        <TabsContent value="mine" className="mt-6 grid sm:grid-cols-2 gap-5">
          {myCoupons.map((mc) => (
            <div key={mc.id} className="bg-[#00E5FF]/15 fk-border-thick rounded-2xl p-6">
              <h3 className="font-display text-xl">{L(mc.coupon?.title_zh, mc.coupon?.title_en)}</h3>
              <span className="font-mono-fk text-lg fk-border rounded px-3 py-1 mt-3 inline-block bg-white">{mc.coupon?.code}</span>
            </div>
          ))}
          {myCoupons.length === 0 && <p className="text-black/50 font-mono-fk">{L("还没有领取优惠券", "No claimed coupons yet")}</p>}
        </TabsContent>

        <TabsContent value="gifts" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {gifts.map((g) => (
            <div key={g.id} className="bg-white fk-border-thick rounded-2xl overflow-hidden fk-lift">
              <img src={img(g.image)} alt="" className="w-full h-40 object-cover border-b-2 border-black" />
              <div className="p-5">
                <h3 className="font-display text-lg">{L(g.title_zh, g.title_en)}</h3>
                <p className="text-xs text-black/60 mt-1">{L(g.description_zh, g.description_en)}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-mono-fk text-sm">{g.points_required} {t("points")}</span>
                  <button onClick={() => redeem(g)} data-testid={`redeem-${g.id}`} className="fk-border rounded-full px-4 py-1.5 text-sm font-semibold bg-[#FF5757] text-white fk-lift">{t("redeem")}</button>
                </div>
              </div>
            </div>
          ))}
          {gifts.length === 0 && <p className="text-black/50 font-mono-fk">暂无礼品</p>}
        </TabsContent>

        <TabsContent value="activity" className="mt-6 space-y-3">
          {activity.workshops?.map((w) => (
            <div key={w.id} className="bg-white fk-border rounded-xl p-4 flex justify-between">
              <span className="font-medium">{w.workshop_title}</span>
              <span className="font-mono-fk text-xs bg-[#00C853] text-white rounded px-2 py-1">{L("已确认", "Confirmed")}</span>
            </div>
          ))}
          {(!activity.workshops || activity.workshops.length === 0) && <p className="text-black/50 font-mono-fk">{L("还没有参与活动", "No activity yet")}</p>}
        </TabsContent>

        <TabsContent value="redeemed" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {redemptions.map((r) => (
            <div key={r.id} className="bg-white fk-border-thick rounded-2xl overflow-hidden" data-testid={`redemption-${r.id}`}>
              <img src={img(r.gift?.image)} alt="" className="w-full h-36 object-cover border-b-2 border-black" />
              <div className="p-4">
                <h3 className="font-display text-lg">{L(r.gift?.title_zh, r.gift?.title_en)}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono-fk text-xs text-black/50">{new Date(r.redeemed_at).toLocaleDateString()}</span>
                  <span className="font-mono-fk text-xs fk-border rounded px-2 py-0.5 bg-[#FFD93D]">-{r.gift?.points_required} {t("points")}</span>
                </div>
                <div className="mt-2 font-mono-fk text-[10px] tracking-widest text-black/40">CODE · {r.id.slice(0, 8).toUpperCase()}</div>
              </div>
            </div>
          ))}
          {redemptions.length === 0 && <p className="text-black/50 font-mono-fk">{L("还没有兑换记录", "No redemptions yet")}</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
